import { useState } from "react";
import { Alert, Button, Form, Input } from "antd";
import { LockOutlined, UserOutlined } from "@ant-design/icons";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";

interface LoginForm {
  username: string;
  password: string;
}

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const from = (loc.state as { from?: string } | null)?.from ?? "/";

  async function onFinish(values: LoginForm) {
    setErr(null);
    setBusy(true);
    try {
      await login(values.username, values.password);
      nav(from, { replace: true });
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background:
          "radial-gradient(900px 600px at 85% -10%, color-mix(in srgb, var(--cyan) 10%, transparent), transparent 60%), " +
          "radial-gradient(1000px 700px at -10% 30%, color-mix(in srgb, var(--green) 7%, transparent), transparent 60%), " +
          "var(--bg-0)",
        padding: 16,
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Cyan glow top-right */}
      <div
        style={{
          position: "absolute",
          top: -160,
          right: -160,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, color-mix(in srgb, var(--cyan) 14%, transparent) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />
      {/* Green glow bottom-left */}
      <div
        style={{
          position: "absolute",
          bottom: -160,
          left: -160,
          width: 480,
          height: 480,
          borderRadius: "50%",
          background:
            "radial-gradient(circle, color-mix(in srgb, var(--green) 10%, transparent) 0%, transparent 70%)",
          pointerEvents: "none",
        }}
      />

      {/* Subtle grid */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          backgroundImage:
            "linear-gradient(color-mix(in srgb, var(--cyan) 3%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in srgb, var(--cyan) 3%, transparent) 1px, transparent 1px)",
          backgroundSize: "44px 44px",
        }}
      />

      {/* Card */}
      <div
        style={{
          position: "relative",
          width: "100%",
          maxWidth: 400,
          background: "color-mix(in srgb, var(--bg-1) 82%, transparent)",
          backdropFilter: "blur(22px)",
          WebkitBackdropFilter: "blur(22px)",
          border: "1px solid color-mix(in srgb, var(--cyan) 18%, transparent)",
          borderRadius: 18,
          padding: "40px 34px 32px",
          boxShadow:
            "var(--shadow-hero), 0 0 0 1px color-mix(in srgb, var(--strong) 3%, transparent) inset, 0 0 60px color-mix(in srgb, var(--cyan) 7%, transparent)",
        }}
      >
        {/* Logo */}
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div
            style={{
              display: "inline-flex",
              width: 64,
              height: 64,
              background: "linear-gradient(135deg, var(--cyan) 0%, var(--green) 100%)",
              borderRadius: 18,
              alignItems: "center",
              justifyContent: "center",
              marginBottom: 18,
              boxShadow:
                "0 12px 36px color-mix(in srgb, var(--cyan) 38%, transparent), 0 0 0 1px color-mix(in srgb, var(--strong) 8%, transparent) inset",
            }}
          >
            <svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="var(--on-primary)" strokeWidth="2.2" strokeLinecap="round">
              <path d="M2 12h4l3-7 4 14 3-7h6"/>
            </svg>
          </div>
          <div
            style={{
              fontSize: 20,
              fontWeight: 700,
              color: "var(--strong)",
              letterSpacing: 0.4,
              marginBottom: 4,
              fontFamily: 'Inter, -apple-system, sans-serif',
            }}
          >
            NetScope Console
          </div>
          <div style={{ fontSize: 13, color: "var(--muted)", letterSpacing: 0.3 }}>
            Network Flow Analytics · Sign in
          </div>
        </div>

        {/* Error */}
        {err && (
          <Alert
            type="error"
            message={err}
            showIcon
            closable
            style={{
              marginBottom: 16,
              background: "color-mix(in srgb, var(--red) 10%, transparent)",
              border: "1px solid color-mix(in srgb, var(--red) 28%, transparent)",
              borderRadius: 8,
              color: "var(--red)",
            }}
            onClose={() => setErr(null)}
          />
        )}

        {/* Form */}
        <Form<LoginForm> layout="vertical" onFinish={onFinish} autoComplete="off">
          <Form.Item
            name="username"
            rules={[{ required: true, message: "Username required" }]}
            style={{ marginBottom: 12 }}
          >
            <Input
              prefix={<UserOutlined style={{ color: "var(--muted)" }} />}
              placeholder="Username"
              size="large"
              style={{
                background: "color-mix(in srgb, var(--strong) 4%, transparent)",
                border: "1px solid color-mix(in srgb, var(--cyan) 15%, transparent)",
                borderRadius: 10,
                color: "var(--text)",
                fontSize: 13,
              }}
            />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[{ required: true, message: "Password required" }]}
            style={{ marginBottom: 22 }}
          >
            <Input.Password
              prefix={<LockOutlined style={{ color: "var(--muted)" }} />}
              placeholder="Password"
              size="large"
              style={{
                background: "color-mix(in srgb, var(--strong) 4%, transparent)",
                border: "1px solid color-mix(in srgb, var(--cyan) 15%, transparent)",
                borderRadius: 10,
                color: "var(--text)",
                fontSize: 13,
              }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              size="large"
              loading={busy}
              style={{
                height: 44,
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 600,
                background:
                  "linear-gradient(135deg, var(--cyan) 0%, var(--green) 100%)",
                border: "none",
                color: "var(--on-primary)",
                boxShadow: "0 10px 26px color-mix(in srgb, var(--cyan) 35%, transparent)",
                letterSpacing: 0.5,
              }}
            >
              Sign in
            </Button>
          </Form.Item>
        </Form>

        {/* Footer */}
        <div
          style={{
            textAlign: "center",
            marginTop: 22,
            fontSize: 12,
            color: "var(--faint)",
            letterSpacing: 0.5,
          }}
        >
          NetScope Console · sflow sensor platform
        </div>
      </div>
    </div>
  );
}
